import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import PropTypes from "prop-types";
import * as React from "react";
import { Checkbox, DragIndicatorIcon, Tooltip } from "../../../themeV2";
import css from "./SortableProduct.module.css";

const MULTI_SIGNER_REASON =
  "Only admins can change products with more than one signer";

const SortableProduct = React.memo(function SortableProduct({
  id,
  disabled,
  isAtLimit = false,
  isSelectable = false,
  isSelectionBlocked = false,
  isSelected = false,
  onToggleSelect,
  productName = "",
  children,
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleCheckboxChange = React.useCallback(
    (_, e) => {
      e?.stopPropagation?.();
      onToggleSelect?.(id);
    },
    [onToggleSelect, id],
  );

  const showToolbar = !disabled || isSelectable;

  const wrapperClasses = [css.wrapper, isSelected && css.selected]
    .filter(Boolean)
    .join(" ");

  const toolbarClasses = [css.hoverToolbar, isSelected && css.alwaysVisible]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={wrapperClasses}
    >
      {showToolbar && (
        <div className={toolbarClasses}>
          {!disabled && (
            <button type="button" className={css.dragHandle} {...listeners}>
              <DragIndicatorIcon />
            </button>
          )}
          {isSelectable && !isSelectionBlocked && (
            <Checkbox
              checked={isSelected}
              disabled={!isSelected && isAtLimit}
              onCheckedChange={handleCheckboxChange}
              aria-label={`Select ${productName}`}
              className={css.checkboxInToolbar}
            />
          )}
          {isSelectable && isSelectionBlocked && (
            <Tooltip.Root>
              <Tooltip.Trigger render={<span />}>
                <Checkbox
                  checked={false}
                  disabled
                  aria-label={`Select ${productName}. ${MULTI_SIGNER_REASON}`}
                  className={css.checkboxInToolbar}
                />
              </Tooltip.Trigger>
              <Tooltip.Popup>{MULTI_SIGNER_REASON}</Tooltip.Popup>
            </Tooltip.Root>
          )}
        </div>
      )}
      {children}
    </div>
  );
});

SortableProduct.propTypes = {
  id: PropTypes.number.isRequired,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  isAtLimit: PropTypes.bool,
  isSelectable: PropTypes.bool,
  isSelectionBlocked: PropTypes.bool,
  isSelected: PropTypes.bool,
  onToggleSelect: PropTypes.func,
  productName: PropTypes.string,
};

export default SortableProduct;
